/** TALKING TO SAM — the browser's own speech recognition. No service, no key.
 *
 *  Safari on iPad has this, but WebKit bug 321436 makes the recogniser go deaf
 *  after audio has played — which is our exact case, because Sam speaks first.
 *  So, as agreed:
 *    * the microphone is primed ONCE with getUserMedia, never on every retry,
 *      so the permission prompt is not asked again and again;
 *    * a settle window is left after he finishes speaking before the mic opens;
 *    * a silent or failed attempt is retried with escalating delays.
 *  Interim words are shown when they arrive and never depended on. Typing
 *  always works, whatever this does.
 */
export const RETRIES = [300, 1000, 2000, 3500];
export const SETTLE_AFTER_SPEECH = 700;

const Engine = () => (typeof window === 'undefined' ? null
    : (window.SpeechRecognition || window.webkitSpeechRecognition));

export const canListen = () => Boolean(Engine());

const RECOG_LANG = { en: 'en-CA', fr: 'fr-CA', es: 'es-ES' };

let primed = false;

/** Ask for the microphone once, up front, on a real tap. */
export const prime = async () => {
    if (primed || !navigator.mediaDevices?.getUserMedia) return primed;
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((tr) => tr.stop());
        primed = true;
    } catch (_e) { primed = false; }
    return primed;
};

/**
 *  listen({ lang, onInterim, onFinal, onEnd, onBlocked })
 *  Returns a handle with stop(). One tap starts it; it ends itself on a pause.
 */
export const listen = ({ lang = 'en', onInterim, onFinal, onEnd, onBlocked }) => {
    const Ctor = Engine();
    if (!Ctor) { onBlocked?.('unsupported'); return { stop: () => {} }; }
    let attempt = 0;
    let heard = '';
    let done = false;
    let rec = null;
    let timer = null;

    const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        if (heard.trim()) onFinal?.(heard.trim()); else onEnd?.();
    };

    const run = () => {
        rec = new Ctor();
        rec.lang = RECOG_LANG[lang] || RECOG_LANG.en;
        rec.interimResults = true;      // shown when they arrive, never relied on
        rec.continuous = false;         // it stops itself on a pause
        rec.maxAlternatives = 1;
        rec.onresult = (e) => {
            let interim = '';
            for (let i = e.resultIndex; i < e.results.length; i += 1) {
                const chunk = e.results[i][0].transcript;
                if (e.results[i].isFinal) heard += chunk; else interim += chunk;
            }
            onInterim?.((heard + interim).trim());
        };
        rec.onerror = (e) => {
            const err = e?.error;
            if (err === 'not-allowed' || err === 'service-not-allowed') {
                done = true; onBlocked?.('not-allowed'); return;
            }
            retry();
        };
        rec.onend = () => { if (heard.trim()) finish(); else retry(); };
        try { rec.start(); } catch (_e) { retry(); }
    };

    const retry = () => {
        if (done) return;
        if (attempt >= RETRIES.length) { finish(); return; }
        const wait = RETRIES[attempt];
        attempt += 1;
        timer = setTimeout(run, wait);
    };

    run();
    return {
        stop: () => {
            try { rec?.stop(); } catch (_e) { /* already stopped */ }
            finish();
        },
    };
};
